import { datadogRum } from '@datadog/browser-rum';
import { reactPlugin } from '@datadog/browser-rum-react';
import VERSION from 'VERSION.js';

const HYBRID_CONFIG = {
    enableVerboseLogging: process.env.REACT_APP_ENV !== 'PROD',
    enableGrafanaCorrelation: process.env.REACT_APP_ENABLE_GRAFANA_CORRELATION !== 'false',
    correlationSampleRate: parseFloat(process.env.REACT_APP_CORRELATION_SAMPLE_RATE) || 1.0,
    maxCorrelationAge: 5 * 60 * 1000, // 5 minutes
    flushInterval: process.env.REACT_APP_ENV === 'PROD' ? 15000 : 5000,
    externalMonitoring: process.env.REACT_APP_EXTERNAL_MONITORING === 'true',
    apiKey: process.env.REACT_APP_RUM_API_KEY
};

const STANDARD_CONFIG = {
    service: {
        name: 'overcycle-frontend',
        version: VERSION || 'unknown',
        environment: process.env.REACT_APP_ENV || 'development',
        region: process.env.REACT_APP_REGION_EXT || 'unknown',
        instance_id: generateInstanceId()
    },

    labels: {
        service_name: 'service_name',
        environment: 'environment',
        version: 'version',
        region: 'region',
        instance_id: 'instance_id',
        user_id: 'user_id',
        session_id: 'session_id',
        trace_id: 'trace_id',
        span_id: 'span_id',
        correlation_id: 'correlation_id',
        event_type: 'event_type',
        page_path: 'page_path',
        error_type: 'error_type',
        resource_type: 'resource_type',
        http_method: 'http_method',
        http_status: 'http_status'
    }
};

function generateInstanceId() {
    const browserInfo = navigator.userAgent.substring(0, 20).replace(/[^a-zA-Z0-9]/g, '');
    return `browser-${browserInfo}-${Math.random()
        .toString(36)
        .substring(2, 8)}`;
}

function sanitizeUrl(url, options = {}) {
    if (!url || typeof url !== 'string') return 'unknown';

    try {
        const urlObj = new URL(url, window.location.origin);

        const sensitiveParams = [
            'token',
            'api_key',
            'session_id',
            'auth',
            'key',
            'password',
            'secret',
            'authorization',
            'jwt',
            'bearer',
            ...(options.sensitiveParams || [])
        ];
        sensitiveParams.forEach(param => urlObj.searchParams.delete(param));

        let result = options.pathOnly ? urlObj.pathname : urlObj.toString();

        if (options.pathOnly || result.includes(urlObj.pathname)) {
            const originalPath = options.pathOnly ? result : urlObj.pathname;
            const sanitizedPath = originalPath
                .replace(/\/[a-f0-9-]{36}/g, '/:uuid') // Replace UUIDs
                .replace(/\/[a-f0-9]{24}/g, '/:objectid') // Replace MongoDB ObjectIds
                .replace(/\/\d+/g, '/:id'); // Replace numeric IDs

            if (options.pathOnly) {
                result = sanitizedPath;
            } else {
                urlObj.pathname = sanitizedPath;
                result = urlObj.toString();
            }
        }

        const maxLength = options.maxLength || 100;
        if (result.length > maxLength) {
            result = result.substring(0, maxLength) + '...';
        }
        return result;
    } catch (error) {
        console.warn('URL sanitization failed:', error);
        return 'invalid-url';
    }
}

class DistributedTracingExtension {
    constructor(config) {
        this.config = config;
        this.correlationMap = new Map();
        this.activeSpans = new Map();
        this.grafanaBuffers = {
            traces: [],
            logs: [],
            metrics: new Map()
        };

        this.sessionId = this.generateUUID();
        this.currentTraceId = this.generateW3CTraceId();
        this.rootSpanId = this.generateW3CSpanId();

        this.baseLabels = {
            service_name: STANDARD_CONFIG.service.name,
            environment: STANDARD_CONFIG.service.environment,
            version: STANDARD_CONFIG.service.version,
            region: STANDARD_CONFIG.service.region,
            instance_id: STANDARD_CONFIG.service.instance_id
        };

        this.stats = {
            correlationsCreated: 0,
            backendResponsesCorrelated: 0,
            grafanaEventsSent: 0,
            datadogEventsProcessed: 0,
            errors: 0
        };

        this.correlationStats = {
            successful: 0,
            failed: 0,
            missingHeaders: 0
        };

        this.startTime = Date.now();
        this.initializeFlushTimer();
    }

    generateW3CTraceId() {
        const bytes = new Uint8Array(16);
        crypto.getRandomValues(bytes);
        return Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
    }

    generateW3CSpanId() {
        const bytes = new Uint8Array(8);
        crypto.getRandomValues(bytes);
        return Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
    }

    generateUUID() {
        return crypto.randomUUID
            ? crypto.randomUUID()
            : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
                  const r = (Math.random() * 16) | 0;
                  const v = c === 'x' ? r : (r & 0x3) | 0x8;
                  return v.toString(16);
              });
    }

    // Convert DataDog RUM events to OpenTelemetry format for Grafana
    processDataDogEvent(event) {
        if (Math.random() > HYBRID_CONFIG.correlationSampleRate) return;

        this.stats.datadogEventsProcessed++;

        try {
            // Create OpenTelemetry span from DataDog event
            const otSpan = this.convertToOpenTelemetrySpan(event);
            if (otSpan) {
                this.grafanaBuffers.traces.push(otSpan);
            }

            // Create Loki log entry
            const logEntry = this.convertToLokiLog(event);
            if (logEntry) {
                this.grafanaBuffers.logs.push(logEntry);
            }

            // Update Prometheus metrics
            this.updatePrometheusMetrics(event);
        } catch (error) {
            this.stats.errors++;
            if (HYBRID_CONFIG.enableVerboseLogging) {
                console.error('Error processing DataDog event:', error, event);
            }
        }
    }

    convertToOpenTelemetrySpan(ddEvent) {
        const now = Date.now() * 1000000; // nanoseconds
        const spanId = this.generateW3CSpanId();

        // Map DataDog event types to OpenTelemetry spans
        let operationName = 'unknown';
        let spanKind = 'internal';

        let attributes = {
            ...this.baseLabels,
            user_id: ddEvent.usr?.id || 'anonymous',
            session_id: this.sessionId,
            trace_id: this.currentTraceId,
            span_id: spanId,
            event_type: ddEvent.type,
            timestamp: new Date(ddEvent.date || Date.now()).toISOString(),
            browser_name: ddEvent.session?.type || navigator.userAgent.substring(0, 50),
            viewport_size: `${window.innerWidth}x${window.innerHeight}`,
            geo_country: ddEvent.session?.country_iso_code || 'unknown'
        };

        switch (ddEvent.type) {
            case 'view':
                operationName = 'page_view';
                spanKind = 'client';
                attributes = {
                    ...attributes,
                    page_path: sanitizeUrl(ddEvent.view?.url, { pathOnly: true }),
                    page_url: sanitizeUrl(ddEvent.view?.url),
                    page_title: ddEvent.view?.name || document.title,
                    page_referrer: sanitizeUrl(ddEvent.view?.referrer),
                    loading_time_ns: ddEvent.view?.loading_time || 0,
                    first_contentful_paint_ns: ddEvent.view?.first_contentful_paint || 0,
                    largest_contentful_paint_ns: ddEvent.view?.largest_contentful_paint || 0,
                    cumulative_layout_shift: ddEvent.view?.cumulative_layout_shift || 0,
                    first_input_delay_ns: ddEvent.view?.first_input_delay || 0
                };
                break;

            case 'action':
                operationName = ddEvent.action?.type || 'user_action';
                spanKind = 'client';
                attributes = {
                    ...attributes,
                    action_type: ddEvent.action?.type || 'unknown',
                    action_target: ddEvent.action?.target?.name || 'unknown',
                    action_duration_ns: ddEvent.action?.loading_time || 0,
                    frustration_types: ddEvent.action?.frustration?.type?.join(',') || '',
                    error_count: ddEvent.action?.error?.count || 0,
                    page_path: sanitizeUrl(window.location.href, { pathOnly: true })
                };
                break;

            case 'resource':
                operationName = 'resource_load';
                spanKind = 'client';
                attributes = {
                    ...attributes,
                    resource_url: sanitizeUrl(ddEvent.resource?.url),
                    resource_type: ddEvent.resource?.type || 'unknown',
                    http_method: ddEvent.resource?.method || 'GET',
                    http_status: ddEvent.resource?.status_code || 0,
                    resource_duration_ns: ddEvent.resource?.duration || 0,
                    resource_size_bytes: ddEvent.resource?.size || 0,
                    redirect_duration_ns: ddEvent.resource?.redirect?.duration || 0,
                    dns_duration_ns: ddEvent.resource?.dns?.duration || 0,
                    connect_duration_ns: ddEvent.resource?.connect?.duration || 0,
                    ssl_duration_ns: ddEvent.resource?.ssl?.duration || 0
                };
                break;

            case 'error':
                operationName = 'error';
                spanKind = 'client';
                attributes = {
                    ...attributes,
                    error_type: ddEvent.error?.type || 'unknown',
                    error_message: ddEvent.error?.message || '',
                    error_source: ddEvent.error?.source || 'unknown',
                    error_stack_present: ddEvent.error?.stack ? 'true' : 'false',
                    error_resource_url: sanitizeUrl(ddEvent.error?.resource?.url),
                    error_resource_status: ddEvent.error?.resource?.status_code || 0,
                    error_handled: ddEvent.error?.handling || 'unhandled',
                    page_path: sanitizeUrl(window.location.href, { pathOnly: true })
                };
                break;

            case 'long_task':
                operationName = 'long_task';
                spanKind = 'internal';
                attributes = {
                    ...attributes,
                    task_duration_ns: ddEvent.long_task?.duration || 0,
                    page_path: sanitizeUrl(window.location.href, { pathOnly: true })
                };
                break;

            default:
                if (HYBRID_CONFIG.enableVerboseLogging) {
                    console.log('Unknown DataDog event type:', ddEvent.type);
                }
                return null;
        }

        return {
            traceId: this.currentTraceId,
            spanId: spanId,
            parentSpanId: this.rootSpanId,
            operationName: operationName,
            startTime: (ddEvent.date || Date.now()) * 1000000,
            endTime: now,
            duration: (ddEvent.date ? Date.now() - ddEvent.date : 0) * 1000000,
            tags: attributes,
            process: {
                serviceName: this.baseLabels.service_name,
                tags: [
                    { key: 'hostname', value: window.location.hostname },
                    { key: 'environment', value: this.baseLabels.environment },
                    { key: 'version', value: this.baseLabels.version },
                    { key: 'platform', value: 'browser' },
                    { key: 'source', value: 'datadog-rum' },
                    { key: 'region', value: this.baseLabels.region }
                ]
            }
        };
    }

    convertToLokiLog(ddEvent) {
        const timestamp = ((ddEvent.date || Date.now()) * 1000000).toString();

        let message = this.formatLogMessage(ddEvent);
        let level = this.getLogLevel(ddEvent);

        return {
            stream: {
                service_name: this.baseLabels.service_name,
                environment: this.baseLabels.environment,
                version: this.baseLabels.version,
                region: this.baseLabels.region,
                level: level,
                event_type: ddEvent.type,
                source: 'datadog-rum'
            },
            values: [[timestamp, message]]
        };
    }

    formatLogMessage(ddEvent) {
        const timestamp = new Date(ddEvent.date || Date.now()).toISOString();

        const context = {
            session_id: this.sessionId,
            trace_id: this.currentTraceId,
            user_id: ddEvent.usr?.id || 'anonymous',
            page_path: sanitizeUrl(ddEvent.view?.url || window.location.href, { pathOnly: true }),
            correlation_id: this.generateCorrelationId?.() || ''
        };

        const contextStr = Object.entries(context)
            .filter(([key, value]) => value && value !== 'anonymous' && value !== '')
            .map(([key, value]) => `${key}="${value}"`)
            .join(' ');

        let eventMessage = '';

        switch (ddEvent.type) {
            case 'error':
                eventMessage = `ERROR: ${ddEvent.error?.type || 'Unknown'}: ${ddEvent.error?.message ||
                    'Unknown error'}`;
                if (ddEvent.error?.stack) {
                    eventMessage += ` stack_present=true`;
                }
                if (ddEvent.error?.resource?.url) {
                    eventMessage += ` resource_url="${sanitizeUrl(ddEvent.error.resource.url)}"`;
                }
                break;

            case 'view':
                eventMessage = `PAGE_VIEW: ${ddEvent.view?.name || 'Unknown'} url="${sanitizeUrl(ddEvent.view?.url)}"`;
                if (ddEvent.view?.loading_time) {
                    eventMessage += ` load_time=${ddEvent.view.loading_time}ns`;
                }
                if (ddEvent.view?.first_contentful_paint) {
                    eventMessage += ` fcp=${ddEvent.view.first_contentful_paint}ns`;
                }
                if (ddEvent.view?.largest_contentful_paint) {
                    eventMessage += ` lcp=${ddEvent.view.largest_contentful_paint}ns`;
                }
                break;

            case 'action':
                const frustration =
                    ddEvent.action?.frustration?.type?.length > 0
                        ? ` frustration="${ddEvent.action.frustration.type.join(',')}"`
                        : '';
                eventMessage = `USER_ACTION: ${ddEvent.action?.type || 'unknown'} target="${ddEvent.action?.target
                    ?.name || 'unknown'}"${frustration}`;
                if (ddEvent.action?.loading_time) {
                    eventMessage += ` duration=${ddEvent.action.loading_time}ns`;
                }
                if (ddEvent.action?.error?.count > 0) {
                    eventMessage += ` error_count=${ddEvent.action.error.count}`;
                }
                break;

            case 'resource':
                eventMessage = `RESOURCE: ${ddEvent.resource?.method || 'GET'} "${sanitizeUrl(
                    ddEvent.resource?.url
                )}" status=${ddEvent.resource?.status_code || 0} duration=${ddEvent.resource?.duration || 0}ns`;
                if (ddEvent.resource?.size) {
                    eventMessage += ` size=${ddEvent.resource.size}b`;
                }
                if (ddEvent.resource?.type) {
                    eventMessage += ` type="${ddEvent.resource.type}"`;
                }
                break;

            case 'long_task':
                eventMessage = `LONG_TASK: duration=${ddEvent.long_task?.duration || 0}ns`;
                break;

            default:
                eventMessage = `${ddEvent.type.toUpperCase()}: ${JSON.stringify(ddEvent).substring(0, 200)}`;
                break;
        }

        return `[${timestamp}] ${contextStr} ${eventMessage}`;
    }

    getLogLevel(ddEvent) {
        switch (ddEvent.type) {
            case 'error':
                return 'error';
            case 'action':
                return ddEvent.action?.frustration?.type?.length > 0 ? 'warn' : 'info';
            case 'resource':
                return ddEvent.resource?.status_code >= 400 ? 'warn' : 'info';
            case 'long_task':
                return 'warn';
            default:
                return 'info';
        }
    }

    updatePrometheusMetrics(ddEvent) {
        const baseLabels = {
            ...this.baseLabels,
            event_type: ddEvent.type,
            user_id: ddEvent.usr?.id || 'anonymous'
        };

        // Count all events with standardized metric name
        this.updateMetric('rum_events_total', 1, baseLabels, 'counter');

        // Type-specific metrics
        switch (ddEvent.type) {
            case 'view':
                if (ddEvent.view?.loading_time) {
                    this.updateMetric(
                        'rum_page_load_duration_ns',
                        ddEvent.view.loading_time,
                        {
                            ...baseLabels,
                            page_path: sanitizeUrl(ddEvent.view.url, { pathOnly: true })
                        },
                        'gauge'
                    );
                }

                const webVitals = [
                    { metric: 'first_contentful_paint_ns', value: ddEvent.view?.first_contentful_paint },
                    { metric: 'largest_contentful_paint_ns', value: ddEvent.view?.largest_contentful_paint },
                    { metric: 'cumulative_layout_shift', value: ddEvent.view?.cumulative_layout_shift },
                    { metric: 'first_input_delay_ns', value: ddEvent.view?.first_input_delay }
                ];

                webVitals.forEach(({ metric, value }) => {
                    if (value !== undefined && value !== null) {
                        this.updateMetric(`rum_${metric}`, value, baseLabels, 'gauge');
                    }
                });
                break;

            case 'resource':
                if (ddEvent.resource?.duration) {
                    this.updateMetric(
                        'rum_resource_duration_ns',
                        ddEvent.resource.duration,
                        {
                            ...baseLabels,
                            resource_type: ddEvent.resource.type || 'unknown',
                            http_status: ddEvent.resource.status_code?.toString() || 'unknown',
                            http_method: ddEvent.resource.method || 'GET'
                        },
                        'gauge'
                    );
                }
                break;

            case 'error':
                this.updateMetric(
                    'rum_errors_total',
                    1,
                    {
                        ...baseLabels,
                        error_type: ddEvent.error?.type || 'unknown',
                        error_source: ddEvent.error?.source || 'unknown',
                        page_path: sanitizeUrl(window.location.href, { pathOnly: true })
                    },
                    'counter'
                );
                break;

            case 'action':
                this.updateMetric(
                    'rum_user_actions_total',
                    1,
                    {
                        ...baseLabels,
                        action_type: ddEvent.action?.type || 'unknown',
                        target_element: ddEvent.action?.target?.name || 'unknown',
                        page_path: sanitizeUrl(window.location.href, { pathOnly: true })
                    },
                    'counter'
                );

                if (ddEvent.action?.frustration?.type?.length > 0) {
                    this.updateMetric(
                        'rum_user_frustrations_total',
                        1,
                        {
                            ...baseLabels,
                            frustration_types: ddEvent.action.frustration.type.join(','),
                            page_path: sanitizeUrl(window.location.href, { pathOnly: true })
                        },
                        'counter'
                    );
                }
                break;

            case 'long_task':
                if (ddEvent.long_task?.duration) {
                    this.updateMetric('rum_long_task_duration_ns', ddEvent.long_task.duration, baseLabels, 'gauge');
                }
                break;
        }
    }

    updateMetric(name, value, labels, type) {
        const metricKey = `${name}_${JSON.stringify(labels)}`;

        if (!this.grafanaBuffers.metrics.has(metricKey)) {
            this.grafanaBuffers.metrics.set(metricKey, {
                name: name,
                type: type,
                value: 0,
                labels: labels,
                timestamp: Date.now()
            });
        }

        const metric = this.grafanaBuffers.metrics.get(metricKey);
        if (type === 'counter') {
            metric.value += value;
        } else if (type === 'gauge') {
            metric.value = value;
        }
        metric.timestamp = Date.now();
    }

    // Enhanced API correlation with standardized headers
    enhanceApiRequest(url, options, datadogContext) {
        const correlationId = this.generateUUID();
        const requestSpanId = this.generateW3CSpanId();

        // Store the request context for correlation
        this.activeSpans.set(requestSpanId, {
            url: url,
            method: options.method || 'GET',
            startTime: Date.now(),
            correlationId: correlationId,
            datadogContext: datadogContext
        });

        // Enhanced headers with standardized correlation
        const headers = new Headers(options.headers || {});

        // Standard RUM correlation headers
        headers.set('X-RUM-Session-ID', this.sessionId);
        headers.set('X-RUM-Trace-ID', this.currentTraceId);
        headers.set('X-RUM-Span-ID', requestSpanId);
        headers.set('X-Correlation-ID', correlationId);

        // Environment and service info
        headers.set('X-RUM-Environment', this.baseLabels.environment);
        headers.set('X-RUM-Service', this.baseLabels.service_name);
        headers.set('X-RUM-Version', this.baseLabels.version);
        headers.set('X-RUM-Region', this.baseLabels.region);
        headers.set('X-RUM-Instance-ID', this.baseLabels.instance_id);

        // W3C trace context
        headers.set('traceparent', `00-${this.currentTraceId}-${requestSpanId}-01`);

        // Add DataDog correlation if available
        if (datadogContext?.traceId && datadogContext?.spanId) {
            headers.set('X-DD-Trace-ID', datadogContext.traceId);
            headers.set('X-DD-Span-ID', datadogContext.spanId);
        }

        const userId = this.getUserId();
        if (userId && userId !== 'anonymous') {
            headers.set('X-User-ID', userId);
        }

        return {
            enhancedOptions: { ...options, headers },
            requestSpanId: requestSpanId,
            correlationId: correlationId
        };
    }

    processApiResponse(requestSpanId, response) {
        const spanContext = this.activeSpans.get(requestSpanId);
        if (!spanContext) return;

        const duration = Date.now() - spanContext.startTime;

        // Check for backend correlation
        const backendTraceId = response?.headers?.get('X-Backend-Trace-ID');
        const backendSpanId = response?.headers?.get('X-Backend-Span-ID');

        if (backendTraceId && backendSpanId && HYBRID_CONFIG.enableGrafanaCorrelation) {
            this.createCorrelationSpan(requestSpanId, backendTraceId, backendSpanId, {
                'request.url': spanContext.url,
                'request.method': spanContext.method,
                'request.duration': duration,
                'response.status': response?.status || 0,
                'backend.processing_time': response?.headers?.get('X-Processing-Time') || '0',
                'dd.trace_id': spanContext.datadogContext?.traceId || '',
                'dd.span_id': spanContext.datadogContext?.spanId || ''
            });

            this.stats.backendResponsesCorrelated++;
            this.correlationStats.successful++;
            this.updateMetric('rum_correlation_successful_total', 1, this.baseLabels, 'counter');
        } else {
            this.correlationStats.failed++;
            this.updateMetric('rum_correlation_failed_total', 1, this.baseLabels, 'counter');
        }

        if (!response?.headers?.get('X-Correlation-ID')) {
            this.correlationStats.missingHeaders++;
            this.updateMetric('rum_correlation_missing_headers_total', 1, this.baseLabels, 'counter');
        }

        // Clean up
        this.activeSpans.delete(requestSpanId);
    }

    createCorrelationSpan(frontendSpanId, backendTraceId, backendSpanId, attributes) {
        const correlationSpan = {
            traceId: this.currentTraceId,
            spanId: this.generateW3CSpanId(),
            parentSpanId: frontendSpanId,
            operationName: 'backend_correlation',
            startTime: Date.now() * 1000000,
            endTime: Date.now() * 1000000,
            duration: 0,
            tags: {
                ...this.baseLabels,
                'correlation.type': 'frontend-backend-datadog',
                'backend.trace.id': backendTraceId,
                'backend.span.id': backendSpanId,
                'frontend.span.id': frontendSpanId,
                'user.id': this.getUserId(),
                ...attributes
            },
            links: [
                {
                    traceId: backendTraceId,
                    spanId: backendSpanId,
                    attributes: {
                        'link.type': 'backend-correlation',
                        'link.source': 'datadog-rum'
                    }
                }
            ]
        };

        this.grafanaBuffers.traces.push(correlationSpan);
        this.stats.correlationsCreated++;

        if (HYBRID_CONFIG.enableVerboseLogging) {
            console.log('🔗 Created DataDog correlation span:', {
                frontendSpanId,
                backendTraceId,
                backendSpanId,
                correlationSpan: correlationSpan.spanId
            });
        }
    }

    async sendToGrafanaStack() {
        await Promise.all([this.sendTracesToTempo(), this.sendLogsToLoki(), this.sendMetricsToBackend()]);
    }

    async sendTracesToTempo() {
        if (this.grafanaBuffers.traces.length === 0) return;

        try {
            const traces = [...this.grafanaBuffers.traces];
            this.grafanaBuffers.traces = [];

            const otlpPayload = {
                resourceSpans: [
                    {
                        resource: {
                            attributes: [
                                { key: 'service.name', value: { stringValue: this.baseLabels.service_name } },
                                { key: 'service.version', value: { stringValue: this.baseLabels.version } },
                                { key: 'environment', value: { stringValue: this.baseLabels.environment } },
                                { key: 'region', value: { stringValue: this.baseLabels.region } }
                            ]
                        },
                        scopeSpans: [
                            {
                                scope: {
                                    name: 'datadog-rum-hybrid',
                                    version: '2.0.0'
                                },
                                spans: traces.map(trace => ({
                                    traceId: trace.traceId,
                                    spanId: trace.spanId,
                                    parentSpanId:
                                        trace.parentSpanId === this.rootSpanId ? undefined : trace.parentSpanId,
                                    name: trace.operationName,
                                    kind: trace.tags['span.kind'] === 'client' ? 3 : 1, // CLIENT = 3, INTERNAL = 1
                                    startTimeUnixNano: trace.startTime.toString(),
                                    endTimeUnixNano: (trace.startTime + trace.duration).toString(),
                                    attributes: Object.entries(trace.tags).map(([key, value]) => ({
                                        key,
                                        value: { stringValue: value.toString() }
                                    })),
                                    status: { code: 1 }
                                }))
                            }
                        ]
                    }
                ]
            };

            const headers = {
                'Content-Type': 'application/json',
                'X-Service-Origin': 'datadog-rum-hybrid',
                'X-Environment': this.baseLabels.environment,
                'X-Service-Name': this.baseLabels.service_name
            };

            if (HYBRID_CONFIG.externalMonitoring && HYBRID_CONFIG.apiKey) {
                headers['X-API-Key'] = HYBRID_CONFIG.apiKey;
                headers['Authorization'] = `Bearer ${HYBRID_CONFIG.apiKey}`;
            }

            if (this.sessionId) headers['X-RUM-Session-ID'] = this.sessionId;
            if (this.currentTraceId) headers['X-RUM-Trace-ID'] = this.currentTraceId;

            const response = await fetch(`${this.config.tempoEndpoint}/v1/traces`, {
                method: 'POST',
                headers,
                body: JSON.stringify(otlpPayload)
            });

            if (response.ok) {
                this.stats.grafanaEventsSent++;
                if (HYBRID_CONFIG.enableVerboseLogging) {
                    console.log(`📊 Sent ${traces.length} spans to Tempo via OTLP`);
                }
            } else {
                const errorText = await response.text();
                throw new Error(`Tempo response: ${response.status} - ${errorText}`);
            }
        } catch (error) {
            this.stats.errors++;
            if (HYBRID_CONFIG.enableVerboseLogging) {
                console.error('Error sending traces to Tempo:', error);
            }
        }
    }

    async sendLogsToLoki() {
        if (this.grafanaBuffers.logs.length === 0) return;

        try {
            const logs = [...this.grafanaBuffers.logs];
            this.grafanaBuffers.logs = [];

            const headers = {
                'Content-Type': 'application/json',
                'X-Service-Origin': 'datadog-rum-hybrid',
                'X-Environment': this.baseLabels.environment,
                'X-Service-Name': this.baseLabels.service_name
            };

            if (HYBRID_CONFIG.externalMonitoring && HYBRID_CONFIG.apiKey) {
                headers['X-API-Key'] = HYBRID_CONFIG.apiKey;
            }

            const response = await fetch(`${this.config.lokiEndpoint}/loki/api/v1/push`, {
                method: 'POST',
                headers,
                body: JSON.stringify({ streams: logs })
            });

            if (response.ok) {
                this.stats.grafanaEventsSent++;
                if (HYBRID_CONFIG.enableVerboseLogging) {
                    console.log(`📝 Sent ${logs.length} logs to Loki`);
                }
            } else {
                throw new Error(`Loki response: ${response.status}`);
            }
        } catch (error) {
            this.stats.errors++;
            if (HYBRID_CONFIG.enableVerboseLogging) {
                console.error('Error sending logs to Loki:', error);
            }
        }
    }

    async sendMetricsToBackend() {
        if (this.grafanaBuffers.metrics.size === 0) return;

        try {
            const metrics = Array.from(this.grafanaBuffers.metrics.values());
            this.grafanaBuffers.metrics.clear();

            const payload = {
                service: this.baseLabels.service_name,
                environment: this.baseLabels.environment,
                version: this.baseLabels.version,
                region: this.baseLabels.region,
                instanceId: this.baseLabels.instance_id,
                timestamp: Date.now(),
                traceId: this.currentTraceId,
                userId: this.getUserId(),
                context: {
                    url: window.location.href,
                    pathname: window.location.pathname,
                    userAgent: navigator.userAgent,
                    viewport: `${window.innerWidth}x${window.innerHeight}`,
                    externalMonitoring: HYBRID_CONFIG.externalMonitoring
                },
                metrics: metrics.map(metric => ({
                    name: metric.name,
                    value: metric.value,
                    labels: {
                        ...metric.labels,
                        page_path: sanitizeUrl(window.location.href, { pathOnly: true }),
                        user_agent: navigator.userAgent.substring(0, 100)
                    },
                    type: metric.type,
                    timestamp: metric.timestamp
                }))
            };

            const headers = {
                'Content-Type': 'application/json',
                'X-Service-Origin': 'datadog-rum-hybrid',
                'X-Environment': this.baseLabels.environment,
                'X-Service-Name': this.baseLabels.service_name
            };

            if (HYBRID_CONFIG.apiKey) {
                headers['X-API-Key'] = HYBRID_CONFIG.apiKey;
            }

            if (this.sessionId) headers['X-RUM-Session-ID'] = this.sessionId;
            if (this.currentTraceId) headers['X-RUM-Trace-ID'] = this.currentTraceId;

            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/metrics/rum`, {
                method: 'POST',
                headers,
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                const result = await response.json();
                this.stats.grafanaEventsSent++;
                if (HYBRID_CONFIG.enableVerboseLogging) {
                    console.log(
                        `📈 Sent ${result.processed}/${result.total} metrics to backend (available in Prometheus at /metrics)`
                    );
                }
            } else {
                const errorText = await response.text();
                throw new Error(`Backend metrics response: ${response.status} - ${errorText}`);
            }
        } catch (error) {
            this.stats.errors++;
            if (HYBRID_CONFIG.enableVerboseLogging) {
                console.error('Error sending metrics to backend:', error);
            }
        }
    }

    initializeFlushTimer() {
        this.flushTimer = setInterval(() => {
            this.sendToGrafanaStack();
        }, HYBRID_CONFIG.flushInterval);
    }

    getUserId() {
        return window.currentUser?.id || 'anonymous';
    }

    getSessionId() {
        return this.sessionId;
    }

    getCurrentTraceId() {
        return this.currentTraceId;
    }

    getStats() {
        return {
            ...this.stats,
            configuration: this.baseLabels,
            correlation: this.correlationStats,
            correlationHealthScore: this.calculateCorrelationHealth(),
            sessionId: this.sessionId,
            traceId: this.currentTraceId,
            uptime: Date.now() - this.startTime,
            bufferSizes: {
                traces: this.grafanaBuffers.traces.length,
                logs: this.grafanaBuffers.logs.length,
                metrics: this.grafanaBuffers.metrics.size
            },
            activeSpans: this.activeSpans.size
        };
    }

    destroy() {
        // Clear flush timer
        if (this.flushTimer) {
            clearInterval(this.flushTimer);
        }

        // Final flush
        this.sendToGrafanaStack();

        if (HYBRID_CONFIG.enableVerboseLogging) {
            console.log('🏁 Hybrid DataDog RUM Extension destroyed. Final stats:', this.getStats());
        }
    }
}

// Enhanced fetch interceptor that works with DataDog
function createHybridFetchInterceptor(tracingExtension) {
    const originalFetch = window.fetch;

    return async function(...args) {
        const url = args[0];
        const options = args[1] || {};

        // Skip monitoring for certain URLs
        if (
            typeof url !== 'string' ||
            url.startsWith('data:') ||
            url.startsWith('blob:') ||
            url.includes('favicon') ||
            url.includes('datadog') ||
            url.includes('/api/traces') ||
            url.includes('/loki/api') ||
            url.includes('/api/v1/write') ||
            url.includes('/metrics')
        ) {
            return originalFetch.apply(this, args);
        }

        // Get current DataDog context if available
        const datadogContext = window.DD_RUM?.getInternalContext?.() || {};

        const { enhancedOptions, requestSpanId, correlationId } = tracingExtension.enhanceApiRequest(
            url,
            options,
            datadogContext
        );

        try {
            const response = await originalFetch.call(this, url, enhancedOptions);
            tracingExtension.processApiResponse(requestSpanId, response);
            return response;
        } catch (error) {
            tracingExtension.processApiResponse(requestSpanId, null);
            throw error;
        }
    };
}

// Main initialization function
function initializeHybridDataDogRUM(version, options = {}) {
    const env = process.env.REACT_APP_ENV || 'development';

    // IMPORTANT: Use safe/dummy credentials to prevent DataDog charges
    // This ensures DataDog RUM collection works locally but never sends to DataDog servers
    const safeCredentials = {
        applicationId: `hybrid-rum-${env}-${Math.random()
            .toString(36)
            .substring(2, 6)}`,
        clientToken: `dummy-token-${env}-${Math.random()
            .toString(36)
            .substring(2, 9)}`,
        site: 'datadoghq.com',
        service: STANDARD_CONFIG.service.name
    };

    // Initialize DataDog RUM with SAFE credentials (never sends to DataDog)
    const datadogConfig = {
        applicationId: safeCredentials.applicationId,
        clientToken: safeCredentials.clientToken,
        site: safeCredentials.site,
        service: STANDARD_CONFIG.service.name,
        env: STANDARD_CONFIG.service.environment,
        version: STANDARD_CONFIG.service.version,
        sessionSampleRate: env === 'PROD' ? 10 : 100,
        sessionReplaySampleRate: 0,
        defaultPrivacyLevel: 'mask-user-input',
        plugins: [reactPlugin({ router: false })],
        allowedTracingUrls: [process.env.REACT_APP_API_URL],
        silentMultipleInit: true
    };

    // Enhanced configuration for our Grafana extension
    const extensionConfig = {
        service: STANDARD_CONFIG.service.name,
        env: STANDARD_CONFIG.service.environment,
        version: STANDARD_CONFIG.service.version,
        region: STANDARD_CONFIG.service.region,
        instanceId: STANDARD_CONFIG.service.instance_id,
        apiKey: options.monitoringApiKey || process.env.REACT_APP_RUM_API_KEY,
        tempoEndpoint: options.tempoEndpoint || process.env.REACT_APP_TEMPO_ENDPOINT,
        lokiEndpoint: options.lokiEndpoint || process.env.REACT_APP_LOKI_ENDPOINT
    };

    let tracingExtension = null;

    if (HYBRID_CONFIG.enableGrafanaCorrelation && extensionConfig.apiKey) {
        tracingExtension = new DistributedTracingExtension(extensionConfig);

        // Initialize DataDog with our custom beforeSend hook that intercepts ALL events
        datadogConfig.beforeSend = event => {
            try {
                if (tracingExtension) {
                    tracingExtension.processDataDogEvent(event);
                }
            } catch (error) {
                if (HYBRID_CONFIG.enableVerboseLogging) {
                    console.error('Error processing DataDog event in hybrid mode:', error);
                }
            }

            // ALWAYS return false to prevent DataDog from sending data to their servers
            // This ensures we never get charged by DataDog - all data goes to Grafana only
            return false;
        };

        // Replace fetch with our enhanced interceptor
        window.fetch = createHybridFetchInterceptor(tracingExtension);
    } else {
        // If no Grafana correlation, still prevent DataDog from sending data
        datadogConfig.beforeSend = event => {
            if (HYBRID_CONFIG.enableVerboseLogging) {
                console.log('DataDog event intercepted (no Grafana extension):', event.type);
            }
            return false; // Never send to DataDog
        };
    }

    // Initialize DataDog RUM with safe credentials
    datadogRum.init(datadogConfig);

    if (HYBRID_CONFIG.enableVerboseLogging) {
        console.log('✅ Hybrid DataDog RUM initialized with SAFE credentials (no DataDog charges)');

        // Log configuration
        console.log('🔧 Hybrid RUM Configuration:', {
            datadogEnabled: true,
            sendToDataDog: false,
            datadogCredentials: 'SAFE/DUMMY (no billing)',
            grafanaCorrelationEnabled: !!tracingExtension,
            standardLabels: STANDARD_CONFIG.labels,
            baseLabels: tracingExtension?.baseLabels || {},
            endpoints: extensionConfig,
            sampleRate: HYBRID_CONFIG.correlationSampleRate
        });

        console.log('🛡️ DataDog Safety: Using dummy credentials - no data will be sent to DataDog servers');
    }

    return {
        rumService: tracingExtension,
        datadogRum: datadogRum,
        environment: env,
        configuration: extensionConfig,
        standardConfig: STANDARD_CONFIG,
        correlationEnabled: !!tracingExtension,
        usingDataDogInstrumentation: true,
        sendingToDataDog: false,
        getStats: () => tracingExtension?.getStats() || { error: 'Extension not initialized' },
        getSessionId: () => tracingExtension?.getSessionId() || null,
        getCurrentTraceId: () => tracingExtension?.getCurrentTraceId() || null,
        trackUserAction: (action, target, metadata) => {
            if (HYBRID_CONFIG.enableVerboseLogging) {
                console.log('🎯 User action tracked via DataDog:', { action, target });
            }
        },
        trackPageView: (url, title) => {
            if (HYBRID_CONFIG.enableVerboseLogging) {
                console.log('📄 Page view tracked via DataDog:', { url, title });
            }
        },
        trackError: (error, context) => {
            if (window.DD_RUM) {
                window.DD_RUM.addError(error, context);
            } else if (HYBRID_CONFIG.enableVerboseLogging) {
                console.log('❌ Error tracked (DataDog not ready):', error.message);
            }
        },
        destroy: () => {
            if (tracingExtension) {
                tracingExtension.destroy();
            }
        }
    };
}

export { initializeHybridDataDogRUM, DistributedTracingExtension, createHybridFetchInterceptor };
